import { useMemo, useState } from "react";
import { betonMenge, PRESETS } from "@/lib/beton";
import { trackSettled, track } from "@/lib/track";

const PRESET_LABEL = {
  bodenplatte: "Bodenplatte / Estrich",
  streifenfundament: "Streifenfundament",
  punktfundament: "Punktfundament",
  saeule: "Säule (rund)",
  quader: "Freier Block",
};

const FIELDS = {
  bodenplatte: [["laenge", "Länge"], ["breite", "Breite"], ["dicke", "Dicke"]],
  streifenfundament: [["laenge", "Gesamtlänge"], ["breite", "Breite"], ["tiefe", "Tiefe"]],
  punktfundament: [["anzahl", "Anzahl", "Stk"], ["laenge", "Länge"], ["breite", "Breite"], ["tiefe", "Tiefe"]],
  saeule: [["anzahl", "Anzahl", "Stk"], ["durchmesser", "Durchmesser"], ["hoehe", "Höhe"]],
  quader: [["laenge", "Länge"], ["breite", "Breite"], ["hoehe", "Höhe"]],
};

const DEFAULTS = {
  bodenplatte: { laenge: 500, breite: 400, dicke: 12 },
  streifenfundament: { laenge: 2000, breite: 30, tiefe: 60 },
  punktfundament: { anzahl: 4, laenge: 40, breite: 40, tiefe: 80 },
  saeule: { anzahl: 2, durchmesser: 30, hoehe: 250 },
  quader: { laenge: 100, breite: 100, hoehe: 100 },
};

function NumberField({ label, value, onChange, suffix = "cm" }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center rounded-lg border bg-background focus-within:ring-2 focus-within:ring-primary/40">
        <input
          type="number"
          inputMode="numeric"
          min={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full bg-transparent px-3 py-2 outline-none"
        />
        <span className="px-3 text-sm text-muted-foreground">{suffix}</span>
      </div>
    </label>
  );
}

function Skizze({ preset }) {
  const stroke = "hsl(173 58% 39%)";
  const fill = "hsl(173 58% 39% / 0.18)";
  const txt = "hsl(222 16% 28%)";
  let shapes;
  if (preset === "saeule") {
    shapes = (
      <>
        <rect x="70" y="20" width="40" height="90" rx="4" fill={fill} stroke={stroke} strokeWidth="2" />
        <ellipse cx="90" cy="20" rx="20" ry="7" fill={fill} stroke={stroke} strokeWidth="2" />
        <text x="90" y="128" textAnchor="middle" fontSize="11" fill={txt}>Ø · Höhe</text>
      </>
    );
  } else if (preset === "punktfundament") {
    shapes = (
      <>
        {[30, 95, 160].map((x) => (
          <rect key={x} x={x} y="45" width="40" height="40" rx="3" fill={fill} stroke={stroke} strokeWidth="2" />
        ))}
        <text x="115" y="105" textAnchor="middle" fontSize="11" fill={txt}>n × L · B · Tiefe</text>
      </>
    );
  } else if (preset === "streifenfundament") {
    shapes = (
      <>
        <rect x="20" y="55" width="190" height="22" rx="3" fill={fill} stroke={stroke} strokeWidth="2" />
        <text x="115" y="98" textAnchor="middle" fontSize="11" fill={txt}>Länge · Breite · Tiefe</text>
      </>
    );
  } else {
    // bodenplatte / quader: Quader von schräg
    shapes = (
      <>
        <polygon points="40,80 150,80 180,55 70,55" fill={fill} stroke={stroke} strokeWidth="2" />
        <polygon points="150,80 150,100 180,75 180,55" fill="hsl(173 58% 39% / 0.30)" stroke={stroke} strokeWidth="2" />
        <polygon points="40,80 150,80 150,100 40,100" fill={fill} stroke={stroke} strokeWidth="2" />
        <text x="110" y="120" textAnchor="middle" fontSize="11" fill={txt}>Länge · Breite · {preset === "bodenplatte" ? "Dicke" : "Höhe"}</text>
      </>
    );
  }
  return (
    <svg viewBox="0 0 230 135" className="w-full h-auto max-h-[180px]" role="img" aria-label={`Skizze ${PRESET_LABEL[preset]}`}>
      {shapes}
    </svg>
  );
}

export default function BetonRechner({ preset: initialPreset = "bodenplatte" }) {
  const [preset, setPreset] = useState(PRESETS.includes(initialPreset) ? initialPreset : "bodenplatte");
  const [inputs, setInputs] = useState(DEFAULTS);
  const [zuschlag, setZuschlag] = useState(5);

  const cur = inputs[preset];
  const res = useMemo(() => betonMenge(preset, cur, { zuschlag }), [preset, cur, zuschlag]);

  const set = (patch) => {
    setInputs((p) => ({ ...p, [preset]: { ...p[preset], ...patch } }));
    trackSettled("beton_berechnet", { calculator: preset });
  };
  const switchPreset = (p) => {
    setPreset(p);
    track("beton_preset_gewaehlt", { calculator: p });
  };

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-6 shadow-sm">
      <div className="flex flex-wrap gap-2 mb-5">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => switchPreset(p)}
            data-testid={`preset-${p}`}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
              preset === p ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground hover:border-primary/50"
            }`}
          >
            {PRESET_LABEL[p]}
          </button>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-4">
          {FIELDS[preset].map(([key, label, suffix]) => (
            <NumberField
              key={key}
              label={label}
              value={cur[key]}
              suffix={suffix || "cm"}
              onChange={(v) => set({ [key]: v })}
            />
          ))}
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Zuschlag (Verdichtung/Verschnitt)</span>
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/40 w-fit">
              {[0, 5, 10].map((z) => (
                <button
                  key={z}
                  type="button"
                  onClick={() => setZuschlag(z)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    zuschlag === z ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {z} %
                </button>
              ))}
            </div>
          </label>
        </div>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl bg-secondary/60 p-4">
            <div className="text-sm text-muted-foreground">Benötigter Beton</div>
            <div className="text-2xl font-semibold mt-0.5" data-testid="beton-ergebnis">
              {res.volumenBrutto.toLocaleString("de-DE")} m³
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              inkl. {res.zuschlag} % Zuschlag (netto {res.volumenNetto.toLocaleString("de-DE")} m³)
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-background/70 p-2">
                <div className="text-lg font-semibold">{res.saecke40}</div>
                <div className="text-xs text-muted-foreground">Säcke à 40 kg</div>
              </div>
              <div className="rounded-lg bg-background/70 p-2">
                <div className="text-lg font-semibold">{res.saecke25}</div>
                <div className="text-xs text-muted-foreground">Säcke à 25 kg</div>
              </div>
              <div className="rounded-lg bg-background/70 p-2">
                <div className="text-lg font-semibold">{res.gewicht.toLocaleString("de-DE")}</div>
                <div className="text-xs text-muted-foreground">kg gesamt</div>
              </div>
            </div>
          </div>
          <Skizze preset={preset} />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Schätzung für die Materialbestellung, keine statische Bemessung. Trockenbeton-Ergiebigkeit gerundet (40 kg ≈ 0,02 m³, 25 kg ≈ 0,0125 m³).
          </p>
        </div>
      </div>
    </div>
  );
}
